import { apiClient } from '@/api/api'
import type { AxiosResponse } from 'axios'
import type { UserProfile } from '@/types/user'

export const fetchUserProfile = (): Promise<AxiosResponse<UserProfile>> => {
  return apiClient.get<UserProfile>('/user/profile')
}

// Note: Response is transformed from snake_case to camelCase by caseTransformerInterceptor
export interface UserInfoResponse {
  name: string
  email: string
  tasteSurveyConfigId: number
  sizeSurveyConfigId: number
}

export const fetchAuthUserInfo = (): Promise<AxiosResponse<UserInfoResponse>> => {
  return apiClient.get<UserInfoResponse>('/auth/user-info')
}

// Types for new avatar upload endpoints
export interface GetAvatarUploadUrlRequest {
  content_type: string
  file_size: number
}

// Note: Response is transformed from snake_case to camelCase by caseTransformerInterceptor
export interface GetAvatarUploadUrlResponse {
  uploadUrl: string
  key: string
  expiresIn: number
}

export interface ConfirmAvatarUploadRequest {
  key: string
}

// Note: Response is transformed from snake_case to camelCase by caseTransformerInterceptor
export interface ConfirmAvatarUploadResponse {
  avatarUrl?: string
  avatar?: string
}


export type DeleteAccountReason =
  | 'no_longer_needed'
  | 'found_alternative'
  | 'privacy_concerns'
  | 'too_many_emails'
  | 'difficult_to_use'
  | 'other'

export interface DeleteAccountResponse {
  message: string
  /** Deletion is a soft delete; the account is recoverable until this date. */
  recoveryDeadline: string
  canRecover: boolean
  cognitoDisabled: boolean
}

/**
 * The backend accepts a reason and free-text feedback; the confirm dialog does
 * not collect either yet, so the required enum defaults to "other".
 */
export const deleteAccount = (
  reason: DeleteAccountReason = 'other',
  feedback?: string
): Promise<AxiosResponse<DeleteAccountResponse>> => {
  return apiClient.post<DeleteAccountResponse>('/user/account/delete', {
    reason,
    feedback: feedback ?? null
  })
}

export const deleteUserAvatar = (): Promise<AxiosResponse<void>> => {
  return apiClient.delete<void>('/user/avatar')
}

export const resetUserPreferences = (): Promise<AxiosResponse<void>> => {
  return apiClient.post<void>('/user/preferences/reset')
}

export interface UpdateUserProfileRequest {
  name?: string
  gender?: string | null
  birthday?: string | null
  profile_img_url?: string | null
}

export const updateUserProfile = (data: UpdateUserProfileRequest): Promise<AxiosResponse<UserProfile>> => {
  return apiClient.put<UserProfile>('/user/profile', data)
}

// New avatar upload endpoints
export const getAvatarUploadUrl = (
  data: GetAvatarUploadUrlRequest
): Promise<AxiosResponse<GetAvatarUploadUrlResponse>> => {
  return apiClient.post<GetAvatarUploadUrlResponse>('/user/avatar/upload-url', data)
}

export const confirmAvatarUpload = (
  data: ConfirmAvatarUploadRequest
): Promise<AxiosResponse<ConfirmAvatarUploadResponse>> => {
  return apiClient.post<ConfirmAvatarUploadResponse>('/user/avatar/confirm', data)
}

// Helper function to upload file directly to S3
export async function uploadFileToS3(
  file: File,
  uploadUrl: string,
  contentType: string
): Promise<void> {
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    body: file,
    headers: {
      'Content-Type': contentType,
    },
  })

  if (!response.ok) {
    throw new Error(`Failed to upload file to S3: ${response.statusText}`)
  }
}

// Wrapper function for three-step avatar upload process
export async function uploadAvatar(file: File): Promise<string> {
  const contentType = file.type || 'image/jpeg'
  const fileSize = file.size

  // Step 1: Get presigned upload URL
  const uploadUrlResponse = await getAvatarUploadUrl({
    content_type: contentType,
    file_size: fileSize,
  })

  // Data is transformed from snake_case to camelCase by caseTransformerInterceptor
  const { uploadUrl, key } = uploadUrlResponse.data

  if (!uploadUrl) {
    throw new Error('Upload URL not returned from server')
  }

  // Step 2: Upload file directly to S3
  await uploadFileToS3(file, uploadUrl, contentType)

  // Step 3: Confirm upload
  const confirmResponse = await confirmAvatarUpload({ key })
  // Data is transformed from snake_case to camelCase by caseTransformerInterceptor
  const avatarUrl = confirmResponse.data.avatarUrl || confirmResponse.data.avatar

  if (!avatarUrl) {
    throw new Error('Avatar URL not returned from server')
  }

  return avatarUrl
}

